//EmployeeViewTicket.js

import React, { JSX, useCallback, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Divider,
  Button,
  Rating,
  Link,
} from "@mui/material";
import axios from "../../api";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryModal from "layouts/AdminTickets/HistoryModal";
import FormatAssigneeHistory from "examples/ReuseFunctions/FormatAssigneeHistory";
import FormatStatusHistoryData from "examples/ReuseFunctions/FormatStatusHistory";
import AddConversation from "examples/ReuseFunctions/AddConversation";
import { useSelector } from "react-redux";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import FormatStatusTrackerData from "examples/ReuseFunctions/FormatStatusTracker";
import UploadIcon from "@mui/icons-material/Upload";
import LocationDisplay from "components/LocationDisplay";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { RootState } from "store";
import { AxiosError } from "axios";

const formatDate = (dateString: string | number | Date) => {
  const date = new Date(dateString);
  const optionsDate: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const formattedDate = new Intl.DateTimeFormat("en-US", optionsDate).format(
    date
  );
  const formattedTime = new Intl.DateTimeFormat("en-US", optionsTime).format(
    date
  );
  return `${formattedDate}, ${formattedTime}`;
};

interface Media {
  multimedia_id: number;
  uploaded_by: string;
  media_stage: string | null;
  file_name: string;
  file_type: string;
  file_path: string;
  latitude: number;
  longitude: number;
}

interface Ratings {
  rating_id: number;
  rating: number | null;
  feedback: string | null;
}

interface Ticket {
  multimedia?: Media[] | null;
  title: string;
  status_tracker: string[];
  category_name: string;
  asset_name: string;
  customer_type: string;
  customer_name: string;
  division_name: string;
  customer_phone: string;
  customer_email: string;
  employee_name: string;
  status_name: string;
  address: string;
  city_name: string;
  state_name: string;
  address_type: string;
  description: string;
  rating: Ratings[];
  created_at: string | Date;
  feedback: string;
  customer_reject_reason: string;
  pending_reason: string;
  ticket_id: number;
  status_id: number;
  customer_comments: string;
}

interface Location {
  latitude: number;
  longitude: number;
}

const EmployeeViewTicket = () => {
  const { id } = useParams();
  const { fetchError } = useSnackbar();

  const user = useSelector((state: RootState) => state.auth.user);
  const [ticketData, setTicketData] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [assigneeHistory, setAssigneeHistory] = useState<JSX.Element[] | null>(
    null
  );
  const [statusHistory, setStatusHistory] = useState<JSX.Element[] | null>(
    null
  );
  const fetchedIdRef = React.useRef<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(from);
      return;
    }
    navigate("/employee-tickets-list");
  };

  const fetchData = useCallback(async () => {
    if (!id || fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;
    setLoading(true);
    try {
      const ticketResponse = await axios.get(`/tickets/${id}`);
      const { data } = ticketResponse;
      const ticketData = data?.list;
      if (ticketData?.assignee_history) {
        setAssigneeHistory(FormatAssigneeHistory(ticketData.assignee_history));
      }
      if (ticketData?.status_history) {
        setStatusHistory(FormatStatusHistoryData(ticketData.status_history));
      }
      setTicketData(ticketData);
    } catch (error) {
      navigate("/employee-tickets-list");
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  }, [id, fetchError, navigate]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchedIdRef.current = null;
    };
  }, [fetchData]);

  const handleMediaOpen = (fileName: string) => {
    const url = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${fileName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleStatusOpen = () => {
    setStatusModalOpen(true);
  };

  const handleStatusClose = () => {
    setStatusModalOpen(false);
  };

  const handleAssigneeOpen = () => {
    setAssigneeModalOpen(true);
  };

  const handleAssigneeClose = () => {
    setAssigneeModalOpen(false);
  };

  const getLocation = (): Promise<Location> =>
    new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        (error) => reject(new Error(error.message || "Location access denied"))
      )
    );

  const handleEmployeeMediaUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    mediaStage: string
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    try {
      const { latitude, longitude } = await getLocation();
      const formData = new FormData();
      const isImage = file.type.startsWith("image/");
      const mediaType = isImage ? "Photo" : "Video";

      formData.append("file", file);
      formData.append("ticket", String(ticketData?.ticket_id ?? ""));
      formData.append("media_type", mediaType);
      formData.append("media_stage", mediaStage);
      formData.append("latitude", String(latitude ?? ""));
      formData.append("longitude", String(longitude ?? ""));
      formData.append("uploaded_by", String(user?.userId ?? ""));

      await axios.post("/employee-uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchedIdRef.current = null;
      fetchData();
    } catch (err) {
      console.error("Upload failed:", err);
      fetchError("Upload failed. Try again.");
    }
  };

  if (loading || !ticketData) {
    return <PageLoader />;
  }

  const rawMultimedia = ticketData?.multimedia || [];
  const dedupedMultimedia = rawMultimedia.filter(
    (m, i, arr) => arr.findIndex((x) => x.multimedia_id === m.multimedia_id) === i
  );
  const employeeMedia = dedupedMultimedia.filter((media) => media.media_stage !== null);
  const preUploads = employeeMedia.filter((m) => m.media_stage === "pre");
  const postUploads = employeeMedia.filter((m) => m.media_stage === "post");
  const customerMedia = dedupedMultimedia.filter((m) => m.media_stage === null);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="view-ticket"
        style={{
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="center"
          className="view-ticket-title"
        >
          <IconButton className="back-btn">
            <ArrowBackIcon onClick={handleBack} />
          </IconButton>
          <Typography variant="h5" gutterBottom>
            Ticket Details
          </Typography>
        </Grid>
        {ticketData?.status_tracker && (
          <FormatStatusTrackerData trackingData={ticketData.status_tracker} />
        )}

        <Grid container className="view-ticket-details">
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Title</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.title}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Category</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.category_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Name</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.customer_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Mobile</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.customer_phone}</span>
              </Box>
            </Typography>
          </Grid>

          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Email</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.customer_email}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Created At</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {ticketData?.created_at
                    ? formatDate(ticketData.created_at)
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Assignee</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.employee_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Status</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.status_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Status History</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={() => handleStatusOpen()}
                >
                  Click here
                </Link>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Assignee History</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAssigneeOpen()}
                >
                  Click here
                </Link>
              </Box>
            </Typography>
          </Grid>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={3} md={2} ml={-1} mr={1}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                <span>Address</span>
              </Typography>
            </Grid>

            <Grid item xs={12} sm={9} md={10} ml={-1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <span>
                  :{" "}
                  {`${ticketData?.address}, ${ticketData?.city_name}, ${ticketData?.state_name}, ${ticketData?.address_type}`}
                </span>
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} sx={{ my: 1 }}>
            <Grid container>
              <Grid item xs={4} sm={4} md={2}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="flex-start">
                    <span>Description</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} ml={-1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "normal",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  : {ticketData?.description}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {ticketData?.rating && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Rating</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>
                      :{" "}
                      <Rating
                        name="simple-controlled"
                        value={ticketData?.rating?.[0]?.rating || 0}
                        readOnly
                      />
                    </span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.feedback && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Feedback</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.feedback}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.customer_reject_reason && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Reject Reason</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.customer_reject_reason}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.pending_reason && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Pending Reason</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.pending_reason}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        {(ticketData?.status_id === 2 || ticketData?.status_id === 3) && (
          <>
            <Divider sx={{ height: 2, backgroundColor: "blue" }} />
            <Grid item sm={6} md={6}>
              <Typography variant="h5" gutterBottom>
                Customer
              </Typography>
              <div className="comments-conv">
                {ticketData && user && (
                  <AddConversation
                    user={{
                      ...user,
                      Role: Array.isArray(user.Role) ? user.Role : [user.Role],
                    }}
                    data={ticketData}
                  />
                )}
              </div>
            </Grid>
          </>
        )}
        {customerMedia.length > 0 && (
          <>
            <Divider sx={{ my: 2, height: 2, backgroundColor: "blue" }} />
            <div className="view-ticket-details">
              <Typography variant="h5" gutterBottom>
                Customer Media
              </Typography>
              <div className="upload-media ticket-media">
                {customerMedia.map((media) => (
                  <div
                    className="media-container view-media"
                    key={media.multimedia_id}
                  >
                    <button
                      type="button"
                      onClick={() => handleMediaOpen(media?.file_name)}
                      style={{
                        all: "unset",
                        cursor: "pointer",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {media.file_type === "Photo" ? (
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`media-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        <Divider sx={{ my: 2, height: 2, backgroundColor: "blue" }} />
        <div className="view-ticket-details">
          <Box sx={{ my: 2 }}>
            <Typography variant="h4">
              <strong>Employee Uploaded Media</strong>
            </Typography>

            <Typography variant="h6" sx={{ pt: 3, pb: 3 }}>
              <strong> Issue Evidence</strong>
            </Typography>
            <Grid container spacing={2}>
              {preUploads.map((media) => (
                <Grid item key={media.multimedia_id} xs={12} sm={6} md={3}>
                  <Card
                    sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}
                  >
                    {media.file_type === "Photo" ? (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`pre-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      </button>
                    )}

                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <LocationOnIcon fontSize="small" color="primary" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        <LocationDisplay
                          latitude={media.latitude}
                          longitude={media.longitude}
                        />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2, height: 3, backgroundColor: "blue" }} />
            <Typography variant="h6" sx={{ pt: 5, pb: 3 }}>
              <strong> Resolution Evidence</strong>
            </Typography>
            <Grid container spacing={2}>
              {postUploads.map((media) => (
                <Grid item key={media.multimedia_id} xs={12} sm={6} md={3}>
                  <Card
                    sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}
                  >
                    {media.file_type === "Photo" ? (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`post-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      </button>
                    )}
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <LocationOnIcon fontSize="small" color="primary" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        <LocationDisplay
                          latitude={media.latitude}
                          longitude={media.longitude}
                        />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
          {ticketData?.status_name?.toLowerCase() !== "done" && (
            <Box sx={{ my: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Issue Evidence</Typography>
                  <input
                    accept="image/*,video/*"
                    id="pre-upload"
                    type="file"
                    style={{ display: "none" }}
                    onChange={(e) => handleEmployeeMediaUpload(e, "pre")}
                  />
                  <label htmlFor="pre-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadIcon />}
                    >
                      Upload Pre Media
                    </Button>
                  </label>
                </Grid>

                {preUploads.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6">Resolution Evidence</Typography>
                    <input
                      accept="image/*,video/*"
                      id="post-upload"
                      type="file"
                      style={{ display: "none" }}
                      onChange={(e) => handleEmployeeMediaUpload(e, "post")}
                    />
                    <label htmlFor="post-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<UploadIcon />}
                      >
                        Upload Post Media
                      </Button>
                    </label>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </div>
        {assigneeModalOpen && (
          <HistoryModal
            title="Assignee"
            data={assigneeHistory}
            isOpen={assigneeModalOpen}
            onClose={handleAssigneeClose}
          />
        )}
        {statusModalOpen && (
          <HistoryModal
            title="Status"
            data={statusHistory}
            isOpen={statusModalOpen}
            onClose={handleStatusClose}
          />
        )}
      </Card>
    </DashboardLayout>
  );
};

export default EmployeeViewTicket;
